import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  type QuerySnapshot,
} from 'firebase/firestore';
import { auth, db } from './firebase';

/**
 * Payments/ledger service — reads the per-user transactions subcollection
 * that the Stripe webhook writes into on the backend.
 *
 * Firestore layout (owner-only, enforced by rules in firestore.rules):
 *   users/{uid}/transactions/{txnId}
 *
 * Writes are backend-only (Admin SDK bypasses rules); the client can only
 * read. That keeps the ledger tamper-proof from the browser side.
 */

export type TransactionType = 'payment' | 'refund' | 'dispute';
export type TransactionDirection = 'credit' | 'debit' | 'info';

export interface StoredTransaction {
  id: string;
  type: TransactionType;
  direction: TransactionDirection;
  creditsDelta: number;
  /** Amount in currency's minor unit — paise for INR, cents for USD. */
  amount: number;
  currency: string;
  planId?: string | null;
  paymentIntentId?: string | null;
  chargeId?: string | null;
  disputeId?: string | null;
  stripeEventId?: string | null;
  description?: string;
  /** ISO timestamp for display; derived from Firestore serverTimestamp. */
  createdAt: string;
}

/**
 * Live subscription to the 100 most recent transactions for the signed-in
 * user, newest first. Returns an unsubscribe function the caller should run
 * on unmount. Empty array fires immediately if the user is signed out.
 */
export function watchTransactions(cb: (txns: StoredTransaction[]) => void): () => void {
  const uid = auth.currentUser?.uid;
  if (!uid) { cb([]); return () => {}; }
  const q = query(
    collection(db, 'users', uid, 'transactions'),
    orderBy('createdAt', 'desc'),
    limit(100),
  );
  return onSnapshot(q, (snap: QuerySnapshot) => {
    const items: StoredTransaction[] = snap.docs.map((d) => {
      const data = d.data() as any;
      const createdAt = data.createdAt?.toDate?.() as Date | undefined;
      return {
        id: d.id,
        type: data.type,
        direction: data.direction,
        creditsDelta: data.creditsDelta ?? 0,
        amount: data.amount ?? 0,
        currency: data.currency ?? 'usd',
        planId: data.planId ?? null,
        paymentIntentId: data.paymentIntentId ?? null,
        chargeId: data.chargeId ?? null,
        disputeId: data.disputeId ?? null,
        stripeEventId: data.stripeEventId ?? null,
        description: data.description ?? '',
        createdAt: createdAt ? createdAt.toISOString() : new Date().toISOString(),
      };
    });
    cb(items);
  }, (err) => {
    console.warn('[paymentsService] watchTransactions error', err);
    cb([]);
  });
}

/**
 * Format a Stripe minor-unit amount for display. Handles the two currencies
 * we actually use (USD/INR) and falls back to a generic currency format for
 * anything else. Signed formatting preserves negative amounts for refunds.
 */
export function formatAmount(amount: number, currency: string): string {
  const code = currency?.toUpperCase() || 'USD';
  const major = amount / 100; // USD, INR, EUR, GBP, CNY all use 2-decimal minor units
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      signDisplay: 'exceptZero',
    }).format(major);
  } catch {
    return `${major.toFixed(2)} ${code}`;
  }
}
